import React, { useState, useRef, useEffect } from "react";
import { useSeo } from "../seo";
import { Plus, Minus } from "lucide-react";
import { DynamicNavigation, Footer } from "../components";
import CTA02 from "../components/CTA02Section";
import { useNavLinkClick } from "../hooks/useNavLinkClick";

/* ─── Data ─────────────────────────────────────────────────────────────── */

const visaTypes = [
  {
    subclass: "Subclass 600",
    title: "Visitor Visa",
    desc: "The Visitor Visa (Subclass 600) is commonly used by travellers who wish to visit Australia for tourism, family visits, or short-term business activities.",
    points: [
      "Single or multiple entries",
      "Stays of up to 3, 6, or 12 months (depending on grant)",
      "Tourism and sightseeing",
      "Visiting friends or relatives",
      "Certain business visitor activities",
    ],
    color: "indigo",
  },
  {
    subclass: "Subclass 601",
    title: "Electronic Travel Authority (ETA)",
    desc: "The ETA is available for travellers holding passports from eligible countries and is designed to simplify the travel process for those who qualify.",
    points: [
      "Multiple visits to Australia within 12 months",
      "Each stay is up to 3 months",
      "Applied through the Australian ETA mobile application",
      "Designed for simplified travel access",
    ],
    color: "blue",
  },
  {
    subclass: "Subclass 651",
    title: "eVisitor Visa",
    desc: "The eVisitor visa is available to travellers from certain countries, mainly within Europe, for tourism, family visits, business meetings, or short-term study.",
    points: [
      "Multiple visits to Australia within 12 months",
      "Each visit up to 3 months",
      "Tourism and family visits",
      "Business meetings",
      "Short-term study programs",
    ],
    color: "violet",
  },
];

const colorMap: Record<string, { badge: string; accent: string; dot: string }> = {
  indigo: { badge: "text-indigo-600 bg-indigo-50 border-indigo-100", accent: "from-indigo-500 to-blue-500", dot: "#4f46e5" },
  blue: { badge: "text-blue-600 bg-blue-50 border-blue-100", accent: "from-blue-500 to-cyan-500", dot: "#2563eb" },
  violet: { badge: "text-violet-600 bg-violet-50 border-violet-100", accent: "from-violet-500 to-purple-500", dot: "#7c3aed" },
};

const commonRequirements = [
  "Genuine intention to stay temporarily, with a plan to leave before visa expiry",
  "Sufficient financial resources to support yourself during your stay in Australia",
  "Valid travel documents including a current passport",
  "Compliance with Australian immigration conditions at all times",
  "Travel history that supports the temporary nature of the visit",
  "No outstanding debts to the Australian Government",
  "Meet health requirements as may be required for your nationality",
  "Meet character requirements set by the Department of Home Affairs",
];

const supportingDocs = [
  { label: "Valid passport", icon: "passport" },
  { label: "Proof of financial capacity", icon: "finance" },
  { label: "Travel plans or accommodation details", icon: "travel" },
  { label: "Invitation letters (for family/friend visits)", icon: "invite" },
  { label: "Return travel evidence", icon: "return" },
  { label: "Employment evidence (if applicable)", icon: "work" },
];

const applicationSteps = [
  "Check which visitor visa type suits your situation and nationality",
  "Gather all required supporting documents",
  "Submit your application online via the Department of Home Affairs website",
  "Provide biometric data or attend health checks if requested",
  "Await assessment by the Department of Home Affairs",
  "Receive your electronic visa grant notification with visa conditions",
];

const conditions = [
  {
    title: "No Work Rights",
    desc: "In most cases, visitor visa holders are not permitted to work in Australia. Visitor visas are designed for tourism, family visits, and short business activities only.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="7" width="20" height="14" rx="2" stroke="#4f46e5" strokeWidth="1.8" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 14l8-4M8 10l8 4" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Limited Study",
    desc: "Visitor visa holders may only study for short periods — typically up to three months. Longer study requires an appropriate student visa.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 3L2 9l10 6 10-6-10-6z" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 12.5v5c1.38.92 3 1.5 5 1.5s3.62-.58 5-1.5v-5" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Must Leave Before Expiry",
    desc: "Visitors must leave Australia before their visa expires, unless they have applied for and have been granted a new visa before the expiry date.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#4f46e5" strokeWidth="1.8" />
        <path d="M12 6v6l4 2" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Possible Extensions",
    desc: "Some visitors may be able to apply for another visa before their current visa expires. However, certain visas include conditions restricting further applications while in Australia.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M21 3v4h-4" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const supportServices = [
  "Visitor visa guidance",
  "Eligibility review",
  "Document checklist support",
  "Application preparation",
  "Lodgement guidance",
  "Ongoing updates",
  "Travel planning support",
];

const processSteps = [
  {
    step: "01",
    title: "Check Eligibility",
    desc: "Determine which visitor visa type suits your nationality, travel purpose, and length of stay. Starting with the correct visa subclass is essential.",
    image: "/assets/services-cards/images/visitor-tourist-visas-01.jpg",
  },
  {
    step: "02",
    title: "Prepare Documents",
    desc: "Gather all required documents including your passport, financial evidence, travel plans, and any invitation letters before submitting your application.",
    image: "/assets/services-cards/images/skills-assessment.jpg",
  },
  {
    step: "03",
    title: "Submit Application",
    desc: "Submit your visitor visa application online through the Department of Home Affairs website or via the relevant platform for ETA or eVisitor visas.",
    image: "/assets/services-cards/images/corporate-immigration.jpg",
  },
  {
    step: "04",
    title: "Await Assessment",
    desc: "The Department of Home Affairs will assess your application. Processing times vary based on visa type, nationality, and individual circumstances.",
    image: "/assets/services-cards/images/employer-sponsored-visas.jpg",
  },
  {
    step: "05",
    title: "Receive Visa Grant",
    desc: "Once approved, you will receive an electronic visa grant notification confirming the visa conditions and validity period. Review these carefully before travelling.",
    image: "/assets/services-cards/images/family-partner-visas.jpg",
  },
];

const faqs = [
  {
    q: "Which visa is best for visiting Australia as a tourist?",
    a: "The Visitor Visa (Subclass 600) is the most commonly used visa for tourists visiting Australia. Travellers from eligible countries may qualify for the ETA (Subclass 601) or eVisitor (Subclass 651), which offer simplified application processes.",
  },
  {
    q: "How long can I stay in Australia on a visitor visa?",
    a: "Stay durations depend on the visa type and the specific conditions of the visa grant. The Visitor Visa (Subclass 600) may allow stays of up to 3, 6, or 12 months. The ETA and eVisitor visas generally allow each stay of up to 3 months within a 12-month period.",
  },
  {
    q: "Can I visit family in Australia on a tourist visa?",
    a: "Yes. Visitor visas, including the Subclass 600, ETA, and eVisitor, can be used for visiting family members in Australia. In some cases, an applicant may need to provide an invitation letter from the family member being visited.",
  },
  {
    q: "Can I work on a visitor visa in Australia?",
    a: "In most cases, visitor visa holders are not permitted to work in Australia. Visitor visas are intended for tourism, family visits, and short business activities such as attending meetings or conferences, not general employment.",
  },
  {
    q: "Can I extend my stay in Australia?",
    a: "Visitor visa holders generally cannot extend their stay once in Australia. However, in some circumstances, it may be possible to apply for another visa before the current visa expires. The ability to do so depends on the visa type and conditions, as some visas restrict further applications while in Australia.",
  },
  {
    q: "What documents are needed for an Australia visitor visa?",
    a: "Documents commonly required include a valid passport, proof of financial resources, travel plans or accommodation details, and in some cases an invitation letter from a family member or host. Applicants may also be asked to provide evidence of ties to their home country and intent to return.",
  },
];

/* ─── VisaCard component ─────────────────────────────────────────────────── */

const VisitorVisaCard = ({
  subclass,
  title,
  desc,
  points,
  color,
}: (typeof visaTypes)[0]) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const divRef = useRef<HTMLDivElement>(null);
  const c = colorMap[color];

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!divRef.current) return;
    const bounds = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      className="relative rounded-xl p-0.5 bg-white overflow-hidden shadow-md cursor-pointer w-full"
    >
      {visible && (
        <div
          className="pointer-events-none blur-xl bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 size-48 absolute z-0 transition-opacity duration-300"
          style={{ top: position.y - 96, left: position.x - 96 }}
        />
      )}
      <div className="relative z-10 bg-white p-6 h-full w-full rounded-[10px] flex flex-col">
        <span className={`inline-block text-xs font-semibold border rounded-full px-3 py-1 mb-3 w-max ${c.badge}`}>
          {subclass}
        </span>
        <h3 className="text-base font-bold text-gray-800 mb-2 leading-snug">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-4">{desc}</p>
        <ul className="space-y-2 mt-auto">
          {points.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <span
                className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: `${c.dot}18` }}
              >
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke={c.dot} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {p}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ─── Page ──────────────────────────────────────────────────────────────── */

export default function VisitorTouristVisasAustralia() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<"next" | "prev">("next");
  const [animating, setAnimating] = useState(false);

  const navigate = (dir: "next" | "prev") => {
    if (animating) return;
    const next =
      dir === "next"
        ? (activeStep + 1) % processSteps.length
        : (activeStep - 1 + processSteps.length) % processSteps.length;
    setSlideDir(dir);
    setPrevStep(activeStep);
    setActiveStep(next);
    setAnimating(true);
    setTimeout(() => {
      setPrevStep(null);
      setAnimating(false);
    }, 540);
  };

  const goTo = (idx: number) => {
    if (idx === activeStep || animating) return;
    setSlideDir(idx > activeStep ? "next" : "prev");
    setPrevStep(activeStep);
    setActiveStep(idx);
    setAnimating(true);
    setTimeout(() => {
      setPrevStep(null);
      setAnimating(false);
    }, 540);
  };

  useEffect(() => {
    const t = setInterval(() => navigate("next"), 3500);
    return () => clearInterval(t);
  }, [activeStep, animating]);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];
  const onLinkClick = useNavLinkClick(links);

  useSeo({
    title:
      "Visitor & Tourist Visas Australia | 600, 601 & 651 Visa Help | Hola World Migration",
    description:
      "Visit Australia for holidays, family visits, or business trips. Explore Visitor Visa 600, ETA 601, and eVisitor 651 pathways with expert guidance from Hola World Migration.",
    path: "/visitor-tourist-visas-australia",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Visitor & Tourist Visas Australia", url: "/visitor-tourist-visas-australia" },
    ],
    faqItems: faqs.map((f) => ({ question: f.q, answer: f.a })),
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Hola World Migration" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      <main className="w-full flex flex-col items-center max-w-full">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="bg-gradient-to-b from-[#F5F7FF] via-[#fffbee] to-[#E6EFFF] w-full font-poppins">
          <div className="flex flex-col md:flex-row items-center max-md:text-center justify-between mt-24 pb-16 px-6 sm:px-10 md:px-24 max-w-7xl mx-auto w-full gap-8">
            {/* Left */}
            <div className="flex flex-col items-center md:items-start">
              <button
                type="button"
                className="mt-16 mb-6 flex items-center gap-2 border border-indigo-600 text-indigo-600 text-xs rounded-full px-4 py-1.5 hover:bg-indigo-50 transition"
              >
                <span>Visitor &amp; Tourist Visa Pathways · Australia</span>
              </button>
              <h1
                className="text-gray-900 font-semibold text-4xl sm:text-5xl md:text-6xl max-w-xl leading-tight"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Visitor &amp; Tourist Visas{" "}
                <span className="text-indigo-600">Australia</span>
              </h1>
              <p className="mt-4 text-gray-600 max-w-md text-sm sm:text-base leading-relaxed">
                Visit Australia for holidays, family visits, or short business trips with the right visitor visa.
              </p>
              <p className="mt-3 text-gray-500 max-w-md text-sm leading-relaxed">
                Australia welcomes travellers from around the world who want to experience its cities, natural landscapes, and vibrant culture.
              </p>

            </div>

            {/* Right: 2×2 image grid */}
            <div aria-label="Visitor visa images" className="mt-12 md:mt-0 grid grid-cols-2 gap-6 pb-6 shrink-0">
              <img alt="Visitor Visas Australia" src="/assets/services-cards/images/visitor-tourist-visas-01.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Family Visits Australia" src="/assets/services-cards/images/family-partner-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Travel to Australia" src="/assets/services-cards/images/corporate-immigration.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Australia Tourism" src="/assets/services-cards/images/skilled-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
            </div>
          </div>
        </section>

        {/* ── SECTION 1: OVERVIEW ──────────────────────────────────────── */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/visitor-tourist-visas-01.jpg"
                alt="Visitor & Tourist Visas Australia"
              />
            </div>
            <div className="w-full max-w-lg">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Visitor &amp; Tourist Visas
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6" />
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Visitor visas allow people from around the world to travel to Australia for short stays. These visas are commonly used for tourism, visiting family members, attending business meetings, or participating in short-term educational activities such as conferences or training programs.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Australia offers several visitor visa options depending on the traveller's nationality, travel purpose, and length of stay. Understanding the available visa types and their requirements can help travellers plan their visit more smoothly.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Many travellers visit Australia to explore its iconic destinations, spend time with family and friends, or attend short business engagements. Visitor visas make these trips possible while ensuring that travellers follow Australian immigration rules during their stay.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECTION 2: TYPES OF VISITOR VISAS ───────────────────────── */}
        <section className="w-full bg-blue-50 py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Types of Visitor Visas
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-3 max-w-3xl">
              Australia offers different visitor visa options depending on the traveller's circumstances.
            </p>
            <p className="text-sm text-slate-400 mb-10">
              Choose the option that best suits your nationality, travel purpose, and length of stay.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visaTypes.map((visa, i) => (
                <VisitorVisaCard key={i} {...visa} />
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 3: REQUIREMENTS & APPLICATION PROCESS ────────────── */}
        <section className="w-full bg-white py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <div className="flex flex-col items-center text-center mb-12">
              <h2
                className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Visitor Visa Requirements and Application Process
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
              <p className="text-base text-slate-500 max-w-2xl">
                Applicants for visitor visas may need to meet several basic requirements depending on their visa category and personal circumstances.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Column 1 — Common Requirements */}
              <div className="flex flex-col rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-indigo-600 px-6 py-5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" stroke="white" strokeWidth="2" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-bold text-white tracking-wide uppercase">Common Requirements</h3>
                </div>
                <div className="bg-white flex-1 p-6">
                  <ul className="space-y-3">
                    {commonRequirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                        <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                          <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Column 2 — Supporting Documents */}
              <div className="flex flex-col rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-blue-600 px-6 py-5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-bold text-white tracking-wide uppercase">Supporting Documents</h3>
                </div>
                <div className="bg-white flex-1 p-6">
                  <p className="text-xs text-slate-400 mb-4 font-medium uppercase tracking-wider">Documents may include</p>
                  <ul className="space-y-2.5">
                    {supportingDocs.map((doc, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                          <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {doc.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Column 3 — Application Process */}
              <div className="flex flex-col rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-violet-600 px-6 py-5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 11l3 3L22 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-bold text-white tracking-wide uppercase">Application Steps</h3>
                </div>
                <div className="bg-white flex-1 p-6">
                  <ol className="relative space-y-0">
                    {applicationSteps.map((step, i) => (
                      <li key={i} className="flex gap-4 pb-5 last:pb-0">
                        <div className="flex flex-col items-center shrink-0">
                          <span className="w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center z-10">
                            {i + 1}
                          </span>
                          {i < applicationSteps.length - 1 && (
                            <div className="w-px flex-1 bg-violet-100 mt-1" />
                          )}
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed pt-0.5">{step}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── SECTION 4: CONDITIONS & EXTENSIONS ───────────────────────── */}
        <section className="w-full bg-blue-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Visitor Visa Conditions and Extensions
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-10 max-w-3xl">
              Visitor visas come with certain conditions that travellers must follow while staying in Australia. Carefully reviewing these conditions before travelling is important to ensure compliance with Australian immigration rules.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {conditions.map((c, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all duration-200 p-6 flex flex-col gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                    {c.icon}
                  </div>
                  <h3 className="text-base font-bold text-gray-800 leading-snug">{c.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 5: PLAN YOUR VISIT / SUPPORT ─────────────────────── */}
        <section className="w-full bg-zinc-100 py-24 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <h2
                className="text-4xl md:text-5xl font-semibold text-neutral-800"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Plan Your Visit to Australia
              </h2>
              <p className="text-base text-neutral-600 max-w-2xl mt-4 leading-relaxed">
                Australia remains one of the world's most popular destinations, offering natural wonders, modern cities, beaches, wildlife, events, and multicultural experiences. Understanding the right visitor visa option and preparing the correct documents can help create a smooth travel experience.
              </p>
              <p className="text-base text-neutral-600 max-w-2xl mt-3 leading-relaxed">
                Hola World Migration provides guidance to help travellers understand visitor visa pathways and prepare the required documents for their application.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr_1fr] gap-6">
              {/* Large indigo card */}
              <div className="md:row-span-2 relative rounded-2xl overflow-hidden bg-indigo-600 p-8 flex flex-col justify-between min-h-[360px]">
                <div aria-hidden="true" className="absolute -bottom-4 -right-3 text-[150px] font-black leading-none text-white/10 select-none pointer-events-none">AU</div>
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.8" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="white" strokeWidth="1.8" />
                    </svg>
                  </div>
                  <h3 className="text-white font-semibold text-2xl mb-3 leading-snug">
                    Expert visitor visa guidance for Australia
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed mt-3">
                    From initial eligibility checks through to application lodgement, Hola World Migration is here to support your visit to Australia.
                  </p>
                </div>
                <a
                  href="/contact?from=visitor-visas-page-cta"
                  className="mt-8 inline-flex items-center gap-2 bg-white text-indigo-600 font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-indigo-50 transition w-max"
                >
                  Book a Consultation
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M4.821 11.999h13.43m0 0-6.714-6.715m6.715 6.715-6.715 6.715" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </div>

              {/* Support service cards */}
              {supportServices.map((service, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all duration-200 p-6 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M20 12V22H4V12" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M22 7H2v5h20V7z" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{service}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: APPLICATION PROCESS STEPPER ───────────────────── */}
        <section className="w-full relative overflow-hidden" style={{ background: "#1B31F5" }}>
          <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 pt-16 pb-0 relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-end pr-4 sm:pr-20 pointer-events-none select-none z-0 overflow-hidden"
            >
              <span
                key={`num-${activeStep}`}
                className="animate-process-num text-[320px] sm:text-[420px] md:text-[520px] font-black leading-none"
                style={{ color: "rgba(255,255,255,0.13)", letterSpacing: "-0.04em" }}
              >
                {processSteps[activeStep].step}
              </span>
            </div>

            <h2
              className="relative z-10 text-white font-bold text-3xl sm:text-4xl md:text-5xl text-center mb-12"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              How the Application Process Works
            </h2>

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              {/* Left: step info */}
              <div
                key={activeStep}
                className={`flex-1 max-w-lg ${
                  prevStep !== null
                    ? slideDir === "next"
                      ? "proc-step-enter-right"
                      : "proc-step-enter-left"
                    : ""
                }`}
              >
                <p className="text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Step {processSteps[activeStep].step}
                </p>
                <h3 className="text-white font-bold text-3xl sm:text-4xl mb-4 leading-snug">
                  {processSteps[activeStep].title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10">
                  {processSteps[activeStep].desc}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("prev")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate("next")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Next step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Right: image */}
              <div className="flex-shrink-0 flex items-end justify-center lg:justify-end mb-14">
                <div className="relative w-80 h-64 sm:w-[440px] sm:h-80">
                  {prevStep !== null && prevStep < processSteps.length && (
                    <img
                      src={processSteps[prevStep].image}
                      alt=""
                      aria-hidden="true"
                      className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${
                        slideDir === "next" ? "proc-img-exit-left" : "proc-img-exit-right"
                      }`}
                    />
                  )}
                  <img
                    key={activeStep}
                    src={processSteps[activeStep].image}
                    alt={processSteps[activeStep].title}
                    className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${
                      prevStep !== null
                        ? slideDir === "next"
                          ? "proc-img-enter-right"
                          : "proc-img-enter-left"
                        : ""
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Timeline dots */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
            <div className="flex items-start gap-0 overflow-x-auto pb-12 scrollbar-hide">
              {processSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`flex flex-col items-start flex-1 min-w-[90px] pr-3 transition-opacity duration-300 ${
                    activeStep === i ? "opacity-100" : "opacity-40 hover:opacity-65"
                  }`}
                >
                  <div className="relative w-full flex items-center mb-4" style={{ height: "14px" }}>
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/25" />
                    <span
                      className={`relative z-10 block rounded-full border-2 transition-all duration-300 shrink-0 ${
                        activeStep === i
                          ? "w-3.5 h-3.5 bg-white border-white shadow-[0_0_8px_2px_rgba(255,255,255,0.45)]"
                          : "w-2 h-2 bg-white/30 border-white/40"
                      }`}
                    />
                  </div>
                  <span className={`font-bold text-base sm:text-lg leading-none ${activeStep === i ? "text-white" : "text-white/70"}`}>
                    {s.step}
                  </span>
                  <span className={`text-xs mt-1.5 leading-snug text-left ${activeStep === i ? "text-white font-semibold" : "text-white/55"}`}>
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="w-full bg-gray-50 py-24 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left sticky */}
              <div className="lg:sticky lg:top-24">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Self Service Help</span>
                </div>
                <h2
                  className="text-4xl md:text-5xl font-black text-[#0a2540] mb-8"
                  style={{ fontFamily: "Poppins, sans-serif", lineHeight: "1.1" }}
                >
                  Frequently Asked <br /> Questions
                </h2>
                <p className="text-base text-gray-600 max-w-sm mb-6 leading-relaxed">
                  Have questions about visitor and tourist visas for Australia? Find answers to commonly asked questions below.
                </p>
                <div className="rounded-2xl overflow-hidden w-full max-w-sm">
                  <img
                    src="/assets/faq/faq-image.png"
                    alt="Visitor Visa FAQ"
                    loading="lazy"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* Right: accordion */}
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-white hover:border-blue-300 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? -1 : i)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      aria-expanded={openFaqIndex === i}
                    >
                      <span className="text-base sm:text-lg font-semibold text-[#0a2540] pr-4">{faq.q}</span>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                        {openFaqIndex === i ? (
                          <Minus className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gray-200 mb-6" />
                        <p className="text-base text-gray-700 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ───────────────────────────────────────────────── */}
        <div className="py-8 px-6 md:px-24 bg-white border-t border-gray-100 w-full">
          <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto">
            <strong className="text-gray-500">Disclaimer:</strong> The information on this page is intended as general guidance only and does not constitute legal, migration, or professional advice. Visa requirements, processing times, eligibility criteria, and conditions are subject to change by the Australian Government without notice. Always verify current requirements directly with the Department of Home Affairs or consult a registered migration agent (MARA) before making decisions about your travel or visa application.
          </p>
        </div>

        <CTA02 />
      </main>

      <Footer />
    </div>
  );
}
